/**
 * ESTABLISHMENT FORM MODAL
 * Edit general informations for the establishment
 */
import Space from "@/components/space"
import { t } from "i18next"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "../../model/establishment.store"
import TextInput from "@/components/text-input"
import Modal from "@/components/modal"
import Button from "@/components/button"
import { faSearch, faSave, faTimes } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { toast } from "react-toastify"
import ListButton from "@/components/list-button"
import ToggleButton from "@/components/toggle-button"

interface Props {
  isNew?: boolean
  status: string
  onSave?: () => void
  onClose: () => void
}

export default function EstablishmentFormModal({ isNew, status, onSave, onClose }: Props) {
  const dispatch = useAppDispatch()
  const draft = useAppSelector(establishmentStore.selectors.draft)
  const [isSearchingNaf, setIsSearchingNaf] = useState(false)

  const update =
    (key: "name" | "codeEstablishment" | "address" | "industryCode") =>
    ({ value }) => {
      dispatch(establishmentStore.actions.edit({ key, value }))
    }

  const updateCoordinate =
    (key: "x" | "y") =>
    ({ value }) => {
      dispatch(establishmentStore.actions.edit({ key, value: value === "" ? null : Number(value) }))
    }

  function getCleanSiret() {
    return (draft.codeEstablishment || "").replace(/\D/g, "")
  }

  async function searchNafFromSiret() {
    const siret = getCleanSiret()

    if (siret.length !== 14) {
      toast("Le numéro SIRET doit contenir 14 chiffres.", { type: "warning" })
      return
    }

    setIsSearchingNaf(true)

    try {
      const response = await fetch(
        `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(siret)}&per_page=1`
      )

      if (!response.ok) throw new Error("NAF search failed")

      const data = await response.json()
      const result = data.results?.[0]
      const establishment = result?.matching_etablissements?.find((item) => item.siret === siret)
      const industryCode =
        establishment?.activite_principale ?? result?.siege?.activite_principale ?? result?.activite_principale

      if (!industryCode) {
        toast("Aucun code NAF trouvé pour ce SIRET.", { type: "warning" })
        return
      }

      dispatch(establishmentStore.actions.edit({ key: "industryCode", value: industryCode }))
    } catch (error) {
      console.log(error)
      toast("Impossible de récupérer le code NAF.", { type: "error" })
    } finally {
      setIsSearchingNaf(false)
    }
  }

  return (
    <Modal
      isLarge
      disableOverflow
      status={status}
      title={isNew ? "Ajouter un établissement" : "Modifier un établissement"}
      footer={
        <div className="flex w-full m-[-5px] mt-20">
          <Space />
          <Button onClick={onClose} className="med" icon={faTimes} isWithBorder>
            {t("utils_close")}
          </Button>
          <Button onClick={onSave} className="primary" icon={faSave}>
            {t("utils_save")}
          </Button>
        </div>
      }>
      <div className="flex flex-col">
        <div className="flex gap-4">
          <TextInput title={t("Nom de l'établissement")} value={draft.name} onChange={update("name")} />
          <div className="flex items-end">
            <TextInput
              title={t("Numéro SIRET")}
              value={draft.codeEstablishment}
              onChange={update("codeEstablishment")}
            />
            <div className="mb-[16px] ml-[-6px]">
              <ListButton
                icon={faSearch}
                text="Récupérer le code NAF"
                isInactive={getCleanSiret().length !== 14 || isSearchingNaf}
                onClick={() => {
                  if (getCleanSiret().length === 14 && !isSearchingNaf) {
                    searchNafFromSiret()
                  }
                }}
              />
            </div>
          </div>
          <TextInput title={t("Code NAF")} value={draft.industryCode} onChange={update("industryCode")} />
        </div>

        <div className="flex gap-4">
          <TextInput title="X" type="number" value={draft.x ?? ""} onChange={updateCoordinate("x")} />
          <TextInput title="Y" type="number" value={draft.y ?? ""} onChange={updateCoordinate("y")} />
        </div>

        <div className="flex items-center gap-3 mt-2 mb-6">
          <div className="w-[46px] shrink-0">
            <ToggleButton
              isActive={draft.isDisabled}
              onToogle={() => {
                dispatch(establishmentStore.actions.edit({ key: "isDisabled", value: !draft.isDisabled }))
              }}
            />
          </div>
          <div className="grey-t">{t("Établissement inactif")}</div>
        </div>

        <div className="flex flex-col">
          <div className="text-input-title flex grey-t">{t("Adresse")}</div>
          <textarea
            placeholder="ex: 14 Rue Eugène Freyssinet&#10;75013 Paris"
            className="text-area"
            value={draft.address || ""}
            onChange={(e) => update("address")({ value: e.target.value })}
            rows={4}
          />
        </div>
      </div>
    </Modal>
  )
}
